// Staff card: assign an assessment to a candidate, see results, grade written answers.
// Track A / A7, 6 Sep 2026. Embed on CandidateDetailPage:
//   import AssessmentsCard from '../components/candidates/AssessmentsCard';
//   <AssessmentsCard candidateId={candidate.id} requirementId={selectedRequirementId} />
// Backend: routes/assessments.py (/api/assessments/*). Renders a "switched off" note when the flag is off.
// A7b (6 Sep 2026): "Send invite" / "Remind" open SendInviteDialog, which sends through
// routes/assessment_invites.py. "Copy link" still works exactly as before.
import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Bell, ClipboardList, Copy, Loader2, Mail, RefreshCw, Send, XCircle } from 'lucide-react';
import SendInviteDialog, { InviteHistory } from './SendInviteDialog';

const STATUS_VARIANT = { pending: 'secondary', started: 'outline', submitted: 'default', expired: 'destructive', cancelled: 'destructive' };

function fmt(d) { return d ? new Date(d).toLocaleString() : '-'; }

function ResultDialog({ assignmentId, open, onClose, onGraded }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !assignmentId) return;
    setLoading(true);
    api.get(`/assessments/assignments/${assignmentId}`)
      .then((r) => { setDetail(r.data); setScores(r.data.manual_scores || {}); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load results'))
      .finally(() => setLoading(false));
  }, [open, assignmentId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(scores).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) payload[k] = Number(v); });
      const r = await api.post(`/assessments/assignments/${assignmentId}/grade`, { scores: payload });
      setDetail(r.data);
      toast.success('Grades saved');
      if (onGraded) onGraded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save grades'); } finally { setSaving(false); }
  };

  const qById = {};
  (detail?.template?.questions || []).forEach((q) => { qById[q.id] = q; });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{detail?.template_title || 'Assessment'} - {detail?.candidate_name || ''}</DialogTitle></DialogHeader>
        {loading && <Loader2 className="animate-spin" />}
        {detail && detail.status !== 'submitted' && <p className="text-sm text-muted-foreground">Not submitted yet ({detail.status}).</p>}
        {detail && detail.status === 'submitted' && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant={detail.passed === true ? 'default' : detail.passed === false ? 'destructive' : 'secondary'}>
                {detail.passed === true ? 'Passed' : detail.passed === false ? 'Not passed' : 'No pass mark'}
              </Badge>
              <span>{detail.score} / {detail.max_score}{detail.pct != null ? ` (${detail.pct}%)` : ''}</span>
              {detail.template?.pass_mark_pct != null && <span className="text-muted-foreground">pass mark {detail.template.pass_mark_pct}%</span>}
              {detail.pending_grades > 0 && <Badge variant="outline">{detail.pending_grades} to grade</Badge>}
              <span className="text-muted-foreground ml-auto">submitted {fmt(detail.submitted_at)}{detail.ip ? ` from ${detail.ip}` : ''}</span>
            </div>
            {(detail.results || []).map((r, i) => {
              const q = qById[r.question_id] || {};
              const labelFor = (id) => ((q.options || []).find((o) => o.id === id) || {}).label || id;
              const shown = Array.isArray(r.answer) ? r.answer.map(labelFor).join(', ') : r.kind === 'mcq' ? labelFor(r.answer) : String(r.answer ?? '-');
              return (
                <div key={r.question_id} className="border rounded-md p-3 space-y-1">
                  <div className="font-medium">{i + 1}. {r.prompt} <span className="text-xs text-muted-foreground">({r.kind}{q.weight != null ? `, weight ${q.weight}` : ''})</span></div>
                  <div className="whitespace-pre-wrap">{shown || <span className="text-muted-foreground">no answer</span>}</div>
                  {r.kind === 'mcq' && (
                    <div className="text-xs">
                      {r.correct ? <span className="text-green-700">correct</span> : <span className="text-red-700">incorrect</span>}
                      {q.correct_answer != null && <span className="text-muted-foreground"> - expected {Array.isArray(q.correct_answer) ? q.correct_answer.map(labelFor).join(', ') : labelFor(q.correct_answer)}</span>}
                      <span className="text-muted-foreground"> - {r.score} / {r.max}</span>
                    </div>
                  )}
                  {r.kind === 'text' && (
                    <div className="flex items-center gap-2 text-xs">
                      <span>Score (0-{q.weight ?? 1}):</span>
                      <Input type="number" min={0} max={q.weight ?? 1} step="0.5" className="h-7 w-24"
                        value={scores[r.question_id] ?? ''} onChange={(e) => setScores((s) => ({ ...s, [r.question_id]: e.target.value }))} />
                      {r.pending_grade && <Badge variant="outline">ungraded</Badge>}
                    </div>
                  )}
                  {r.kind === 'rating' && <div className="text-xs text-muted-foreground">self-rating, not scored</div>}
                </div>
              );
            })}
            {(detail.results || []).some((r) => r.kind === 'text') && (
              <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save grades'}</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AssessmentsCard({ candidateId, requirementId }) {
  const [enabled, setEnabled] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [invite, setInvite] = useState(null);      // { recordId, purpose }
  const [sentTick, setSentTick] = useState(0);

  const load = useCallback(async () => {
    if (!candidateId) return;
    try {
      const s = await api.get('/assessments/settings');
      setEnabled(!!s.data.enabled);
      if (!s.data.enabled) return;
      const [t, a] = await Promise.all([api.get('/assessments/templates'), api.get('/assessments/assignments', { params: { candidate_id: candidateId } })]);
      setTemplates(t.data.templates || []);
      setRows(a.data.assignments || []);
      if (!templateId && (t.data.templates || []).length) setTemplateId(t.data.templates[0].id);
    } catch (e) {
      if (e?.response?.status === 503) setEnabled(false);
      else toast.error(e?.response?.data?.detail || 'Could not load assessments');
    }
  }, [candidateId, templateId]);

  useEffect(() => { load(); }, [load]);

  const assign = async () => {
    if (!templateId) return;
    setBusy(true);
    try {
      const r = await api.post('/assessments/assignments', { template_id: templateId, candidate_id: candidateId, requirement_id: requirementId || null });
      toast.success(r.data.reused ? 'An active link already exists - reusing it' : 'Assessment link created (valid 7 days)');
      if (r.data.take_url && navigator.clipboard) { try { await navigator.clipboard.writeText(r.data.take_url); toast.info('Link copied to clipboard'); } catch { /* noop */ } }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this assessment link?')) return;
    try { await api.post(`/assessments/assignments/${id}/cancel`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch { window.prompt('Copy this link', url); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-4 w-4" />Assessments</CardTitle>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {enabled === false && <p className="text-muted-foreground">Assessments are switched off (Settings: assessments_enabled).</p>}
        {enabled && (
          <>
            <div className="flex gap-2 flex-wrap items-center">
              <select className="border rounded-md h-9 px-2 bg-background flex-1 min-w-48" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.length === 0 && <option value="">No active templates</option>}
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title} ({t.question_count} q{t.pass_mark_pct != null ? `, pass ${t.pass_mark_pct}%` : ''})</option>)}
              </select>
              <Button size="sm" onClick={assign} disabled={busy || !templateId}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Assign</>}
              </Button>
            </div>
            {rows.length === 0 && <p className="text-muted-foreground">No assessments yet. Assign one, then send it to the candidate by email or WhatsApp - or copy the link and send it yourself.</p>}
            {rows.map((a) => (
              <div key={a.id} className="border rounded-md p-2 flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[a.status] || 'secondary'}>{a.status}</Badge>
                <span className="font-medium">{a.template_title}</span>
                {a.status === 'submitted' && <span>{a.score} / {a.max_score}{a.pct != null ? ` (${a.pct}%)` : ''}{a.passed === true ? ' - passed' : a.passed === false ? ' - not passed' : ''}</span>}
                {a.pending_grades > 0 && <Badge variant="outline">{a.pending_grades} to grade</Badge>}
                <span className="text-xs text-muted-foreground ml-auto">{a.status === 'submitted' ? `submitted ${fmt(a.submitted_at)}` : `expires ${fmt(a.expires_at)}`}</span>
                {(a.status === 'pending' || a.status === 'started') && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setInvite({ recordId: a.id, purpose: 'invite' })} title="Send this link to the candidate">
                      <Mail className="h-4 w-4 mr-1" />Send invite
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setInvite({ recordId: a.id, purpose: 'reminder' })} title="One polite reminder (blocked inside 24 hours)">
                      <Bell className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {a.take_url && <Button variant="ghost" size="sm" onClick={() => copy(a.take_url)} title="Copy link"><Copy className="h-4 w-4" /></Button>}
                {a.status === 'submitted' && <Button variant="outline" size="sm" onClick={() => setOpenId(a.id)}>Results</Button>}
                {(a.status === 'pending' || a.status === 'started') && <Button variant="ghost" size="sm" onClick={() => cancel(a.id)} title="Cancel link"><XCircle className="h-4 w-4" /></Button>}
              </div>
            ))}
            <InviteHistory candidateId={candidateId} kind="assessment" refreshKey={sentTick} />
          </>
        )}
      </CardContent>
      <ResultDialog assignmentId={openId} open={!!openId} onClose={() => setOpenId(null)} onGraded={load} />
      <SendInviteDialog
        open={!!invite} onClose={() => setInvite(null)}
        candidateId={candidateId} kind="assessment"
        recordId={invite?.recordId} purpose={invite?.purpose || 'invite'}
        onSent={() => { setSentTick((n) => n + 1); load(); }}
      />
    </Card>
  );
}
